<template>
  <div
    :class="
      float({
        class:
          'absolute right-0 mt-2 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-5rem)] w-max min-w-[14rem] overflow-y-auto overscroll-contain px-4 py-3',
      })
    "
  >
    <ul v-if="Capacitor.isNativePlatform()" class="mb-2 grid grid-cols-2 gap-6 pb-2">
      <li class="nav-item">
        <a @click="handleBack()">
          <span class="nav-icon"><ArrowLeftIcon class="h-4 w-4" /></span>
          {{ t('back') }}
        </a>
      </li>
      <li class="nav-item">
        <a @click="handleReload()">
          <span class="nav-icon"><RotateCwIcon class="h-4 w-4" /></span>
          {{ t('reload') }}
        </a>
      </li>
    </ul>

    <template v-for="group in visibleGroups" :key="group.labelKey">
      <HeaderbarNavGroup :label="t(group.labelKey)" :has-visible-items="true">
        <ul>
          <li
            v-for="item in group.items"
            :key="item.labelKey"
            class="nav-item"
            :class="{ 'nav-item-primary': item.emphasis === 'primary' }"
          >
            <router-link :to="item.route!">
              <span class="nav-icon">
                <component :is="item.icon" class="h-4 w-4" />
              </span>
              <span>{{ t(item.labelKey) }}</span>
            </router-link>
          </li>
        </ul>
      </HeaderbarNavGroup>
    </template>

    <ul class="pt-1">
      <li class="nav-item">
        <router-link :to="{ name: 'settings' }">
          <span class="nav-icon"><SettingsIcon class="h-4 w-4" /></span>
          <span>{{ t('settings') }}</span>
        </router-link>
      </li>
      <li v-if="Capacitor.isPluginAvailable('Adyen')" class="nav-item">
        <a @click="AdyenPlugin.openDeviceSelection()">
          <span class="nav-icon"><CreditCardIcon class="h-4 w-4" /></span>
          <span>{{ t('pairCardReader') }}</span>
        </a>
      </li>
      <li class="nav-item nav-item-logout">
        <button @click="handleLogout">
          <span class="nav-icon"><LogOutIcon class="h-4 w-4" /></span>
          <span>{{ t('logout') }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbility } from '@casl/vue';
import { useLogout } from '@/queries/auth/use-logout';
import { RouterLink } from 'vue-router';
import { float } from '@carvia/theme/lib/cva/float';
import { Capacitor } from '@capacitor/core';
import AdyenPlugin from '@/capacitor-plugins/AdyenPlugin.ts';
import HeaderbarNavGroup from './HeaderbarNavGroup.vue';
import { headerbarNavGroups, type HeaderbarNavItem } from '../headerbar-nav.config';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  LogOutIcon,
  RotateCwIcon,
  SettingsIcon,
} from 'lucide-vue-next';

const { t } = useI18n();
const { can } = useAbility();
const { mutate: logoutUser } = useLogout();

const isItemVisible = (item: HeaderbarNavItem) => {
  if (item.can && !can(...item.can)) {
    return false;
  }
  if (item.showUnlessCan && can(...item.showUnlessCan)) {
    return false;
  }
  return true;
};

const filterVisibleItems = (items: HeaderbarNavItem[]) =>
  items.filter((item) => isItemVisible(item));

const visibleGroups = computed(() =>
  headerbarNavGroups
    .map((group) => ({
      ...group,
      items: filterVisibleItems(group.items),
    }))
    .filter((group) => group.items.length > 0),
);

const handleLogout = () => {
  logoutUser();
};

const handleReload = () => {
  window.location.reload();
};

const handleBack = () => {
  window.history.back();
};
</script>

<style scoped>
.nav-item {
  @apply block;

  & > a,
  & > button {
    @apply flex w-full items-center gap-3 rounded-md px-2 py-1 text-left transition-colors;
  }

  & > a:hover,
  & > button:hover {
    @apply bg-obsidian-100;
  }
}

.nav-icon {
  @apply flex w-5 shrink-0 items-center justify-center text-obsidian-500;
}

.nav-item-primary > a > span:last-child {
  @apply font-medium text-hazel-700;
}

.nav-item-logout > button > span:last-child {
  @apply text-obsidian-600;
}

.nav-item-logout:hover > button > span:last-child {
  @apply text-red-600;
}
</style>

<i18n lang="json">
{
  "en": {
    "groupOverview": "Overview",
    "groupBookings": "Bookings",
    "groupFleet": "Fleet",
    "groupFinance": "Finance",
    "groupSales": "Sales",
    "groupPeople": "Users",
    "groupAdministration": "Administration",
    "login": "Login",
    "logout": "Logout",
    "settings": "Settings",
    "vehicleTypes": "Vehicles",
    "stations": "Stations",
    "legalEntities": "Legal entities",
    "handover": "Handovers",
    "bookingList": "Booking list",
    "bookingPlan": "Booking plan",
    "createBooking": "Create Booking",
    "dashboard": "Dashboard",
    "invoices": "Invoices",
    "receivables": "Receivables",
    "paymentTerminals": "Payment Terminals",
    "users": "Users",
    "vehiclePoolManagement": "Vehicle Pool Management",
    "insuranceCases": "Insurance Cases",
    "coupons": "Coupons",
    "yieldManagement": "Yield management",
    "queueInspector": "Queue Inspector",
    "approvalRequests": "Approval Requests",
    "infringements": "Infringements",
    "pairCardReader": "Pair Card Reader",
    "reload": "Reload",
    "createUser": "Create User",
    "trainingVideos": "Training Videos",
    "media": "Media",
    "reviews": "Customer Ratings",
    "websiteLeads": "Website-Leads"
  },
  "de": {
    "groupOverview": "Übersicht",
    "groupBookings": "Buchungen",
    "groupFleet": "Fuhrpark",
    "groupFinance": "Finanzen",
    "groupSales": "Sales",
    "groupPeople": "Benutzer",
    "groupAdministration": "Administration",
    "login": "Login",
    "logout": "Logout",
    "settings": "Einstellungen",
    "vehicleTypes": "Fahrzeuge",
    "stations": "Stationen",
    "legalEntities": "Gesellschaften",
    "handover": "Übergaben & Rückgaben",
    "bookingList": "Buchungsliste",
    "bookingPlan": "Belegungsplan",
    "createBooking": "Buchung erstellen",
    "dashboard": "Dashboard",
    "invoices": "Rechnungen",
    "receivables": "Forderungen",
    "paymentTerminals": "Zahlungsterminale",
    "users": "Benutzer",
    "vehiclePoolManagement": "Fuhrparkmanagement",
    "insuranceCases": "Versicherungsfälle",
    "coupons": "Gutscheine",
    "yieldManagement": "Yield Management",
    "queueInspector": "Queue Inspector",
    "approvalRequests": "Genehmigungsanfragen",
    "infringements": "Ordnungswidrigkeiten",
    "pairCardReader": "Kartenleser verbinden",
    "reload": "Reload",
    "createUser": "Benutzer erstellen",
    "trainingVideos": "Schulungsvideos",
    "media": "Medien",
    "reviews": "Kundenbewertungen",
    "websiteLeads": "Website-Leads"
  },
  "es": {
    "groupOverview": "Resumen",
    "groupBookings": "Reservas",
    "groupFleet": "Flota",
    "groupFinance": "Finanzas",
    "groupSales": "Sales",
    "groupPeople": "Usuarios",
    "groupAdministration": "Administración",
    "login": "Iniciar sesión",
    "logout": "Cerrar sesión",
    "settings": "Ajustes",
    "vehicleTypes": "Vehículos",
    "stations": "Estaciones",
    "legalEntities": "Sociedades",
    "handover": "Entregas y devoluciones",
    "bookingList": "Lista de reservas",
    "bookingPlan": "Plan de ocupación",
    "createBooking": "Crear reserva",
    "dashboard": "Panel",
    "invoices": "Facturas",
    "receivables": "Cobros pendientes",
    "paymentTerminals": "Terminales de pago",
    "users": "Usuarios",
    "vehiclePoolManagement": "Gestión de flota",
    "insuranceCases": "Siniestros",
    "coupons": "Cupones",
    "yieldManagement": "Gestión de rendimiento",
    "queueInspector": "Inspector de colas",
    "approvalRequests": "Solicitudes de aprobación",
    "infringements": "Infracciones administrativas",
    "pairCardReader": "Emparejar lector de tarjetas",
    "reload": "Recargar",
    "createUser": "Crear usuario",
    "trainingVideos": "Vídeos de formación",
    "media": "Medios",
    "reviews": "Valoraciones de clientes",
    "websiteLeads": "Leads web"
  }
}
</i18n>
